import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Printer, Home, AlertTriangle } from "lucide-react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { StepHeader } from "../../../components/kiosk/StepHeader";
import { StepProgress } from "../../../components/kiosk/StepProgress";

const AUTO_RETURN_SECONDS = 10;

export default function BadgeStep({ visitId, visitorName, hostName, purpose }) {
  const navigate = useNavigate();
  const [badge, setBadge] = useState(null);
  const [countdown, setCountdown] = useState(AUTO_RETURN_SECONDS);
  const issuedRef = useRef(false);

  useEffect(() => {
    if (issuedRef.current) return;
    issuedRef.current = true;
    api.post(`/visits/${visitId}/issue-badge`).then(({ data }) => setBadge(data));
  }, [visitId]);

  useEffect(() => {
    if (!badge) return;
    if (countdown <= 0) {
      navigate("/kiosk");
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [badge, countdown, navigate]);

  if (!badge)
    return (
      <div className="text-center text-slate-400 py-12" data-testid="badge-loading">
        Issuing badge...
      </div>
    );

  return (
    <div className="max-w-sm mx-auto text-center space-y-6" data-testid="checkin-badge-step">
      <StepProgress current="badge" />
      <StepHeader icon={BadgeCheck} title="You're All Set" subtitle="Show this pass at security if asked" />
      <div id="badge-print" className="bg-white border border-slate-200 shadow-md rounded-lg overflow-hidden">
        <div className="bg-brand-navy text-white py-3 px-6 flex items-center justify-center gap-2">
          <BadgeCheck size={16} className="text-brand-cyan" />
          <span className="text-xs font-bold uppercase tracking-[0.2em]">Visitor Pass</span>
        </div>
        <div className="p-6 space-y-3">
          <h2 className="text-2xl font-black uppercase text-brand-navy">{visitorName}</h2>
          {badge.visitor_id && (
            <p className="text-xs font-mono text-slate-400" data-testid="badge-visitor-id">
              Visitor ID: #{badge.visitor_id}
            </p>
          )}
          <p className="text-sm text-slate-500">Visiting: {hostName}</p>
          <p className="text-sm text-slate-500">Purpose: {purpose}</p>
          {badge.no_badge_available ? (
            <div className="flex flex-col items-center gap-2 py-4" data-testid="badge-unavailable-notice">
              <AlertTriangle className="text-amber-500" size={32} />
              <p className="text-sm font-bold text-amber-600 uppercase">No badges available</p>
              <p className="text-xs text-slate-400">Notify admin to add badges for this gate. Visitor is checked in.</p>
            </div>
          ) : (
            <>
              <img src={`data:image/png;base64,${badge.qr_code_base64}`} alt="Badge QR" className="mx-auto w-40 h-40" data-testid="badge-qr-code" />
              <p className="font-mono text-lg tracking-widest text-brand-navy" data-testid="badge-number-display">
                {badge.badge_number}
              </p>
            </>
          )}
          <p className="text-xs text-slate-400">{new Date(badge.check_in_time).toLocaleString()}</p>
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          onClick={() => window.print()}
          data-testid="print-badge-button"
          variant="outline"
          disabled={badge.no_badge_available}
          className="flex-1 h-14 rounded-sm border-slate-300 text-slate-700 gap-2"
        >
          <Printer size={18} /> Print
        </Button>
        <Button onClick={() => navigate("/kiosk")} data-testid="badge-done-button" className="flex-1 h-14 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase gap-2">
          <Home size={18} /> Done
        </Button>
      </div>
      <p className="text-xs text-slate-400" data-testid="badge-auto-return-countdown">
        Returning to home screen in {countdown}s
      </p>
    </div>
  );
}
